import {
  AddToPhotos,
  BlockOutlined,
  CheckCircleOutline,
  DeleteOutline,
  Edit,
  HighlightOff,
  InfoOutlined,
  WarningAmber,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import CustomTable from "../../../components/table/CustomTable";
import LoadingButton from "../../../elements/LoadingButton";
import StatusChip from "../../../elements/StatusChip";
import {
  setGlobalLoader,
  showSnackbar,
} from "../../../redux/actions/commonAction";
import { getAllProvision } from "../../../redux/actions/provisionAction";
import { getDateAndTime } from "../../../utils/timeUtils";
import { deleteProvisionApi } from "./../../../api/provisions/provisionApi";
import CheckovDetails from "./checkovDetails";
import LogDetails from "./logDetails";
import MoreActions from "./moreActions";
import { capitalizeWords } from "../../../utils/helpers";
import ActionDialog from "../../../elements/ActionDialog";
import AccountSummary from "./accountSummary";

const ProvisionTable = ({
  data,
  onApply,
  onReject,
  onTerminate,
  isAdmin,
  getLogsById,
  getProvisionOverviewData,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [rowDetails, setRowDetails] = useState(null);
  const [logData, setLogData] = useState(null);
  const [action, setAction] = useState("");
  const [actionRow, setActionRow] = useState(null);
  const [summaryAction, setSummaryAction] = useState("");
  const [summary, setSummary] = useState(null);

  const columns = [
    {
      field: "name",
      label: "Name",
      render: (row) => (
        <Box display="flex" alignItems="center">
          <Tooltip title={row.description}>
            <IconButton color="default" size="small">
              <InfoOutlined />
            </IconButton>
          </Tooltip>
          {row.name}
        </Box>
      ),
    },
    {
      field: "service",
      label: "Services",
    },
    { field: "accountNumber", label: "Account Number" },
    {
      field: "createdDate",
      label: "Created Date",
      render: (row) => {
        const dateTime = getDateAndTime(row.createdDate);
        return <Tooltip title={dateTime}>{dateTime.split("|")[0]}</Tooltip>;
      },
    },
    {
      field: "status",
      label: "Status",
      render: (row) => {
        const { isApplyPlan } = row?.loader;
        return (
          <>
            {isApplyPlan ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <StatusChip status={row?.status} />
            )}
          </>
        );
      },
    },
    {
      field: "checkov",
      label: "Vulnerabilities",
      render: (row) => {
        if (row?.actions?.checkov) {
          const { passed, failed } = row?.actions?.checkov;
          const color =
            failed > 0 ? "error" : passed > 0 ? "success" : "warning";
          const title = `Passed - ${passed},\n  Failed - ${failed}`;
          return (
            <Tooltip title={title}>
              <IconButton
                onClick={() => setRowDetails(row)}
                color={color}
                size="small"
              >
                {failed > 0 ? (
                  <HighlightOff />
                ) : passed > 0 ? (
                  <CheckCircleOutline />
                ) : (
                  <WarningAmber />
                )}
              </IconButton>
            </Tooltip>
          );
        }
        return "-";
      },
    },
    {
      field: "latestCost",
      label: "Forecasted Cost (Current Month)",
      align: "left",
      render: (row) =>
        row?.latestCost ? (
          <Button variant="outlined" onClick={() => handleSummary(row, "cost")}>
            ${row?.latestCost?.toFixed(2)}
          </Button>
        ) : (
          "-"
        ),
    },
    {
      field: "latestSecurity",
      label: "Security Score",
      align: "left",
      render: (row) =>
        row?.latestSecurity ? (
          <Button
            variant="outlined"
            onClick={() => handleSummary(row, "security")}
          >
            {row?.latestSecurity}%
          </Button>
        ) : (
          "-"
        ),
    },
    {
      field: "logs",
      label: "Logs",
      render: (row) => {
        return (
          <Button
            onClick={async () => {
              setLogData(await getLogsById(row?.id));
            }}
            variant="outlined"
            size="small"
          >
            Show
          </Button>
        );
      },
    },
    {
      field: "action",
      label: "Action",
      render: (row) => renderActions(row),
    },
  ];

  const handleSummary = async (row, type) => {
    setSummary(await getProvisionOverviewData(row, type));
    setSummaryAction(type);
  };

  const handleDelete = async (row) => {
    dispatch(setGlobalLoader(true));
    const response = await deleteProvisionApi(row.id);
    if (response) {
      dispatch(getAllProvision());
      dispatch(
        showSnackbar({
          message: `${row?.name} Deleted Successfully`,
          severity: "success",
          open: true,
        })
      );
    }
    setAction("");
    setActionRow(null);
  };

  const openConfirmDialog = (row, action) => {
    setAction(action);
    setActionRow(row);
  };

  const handleConfirmedAction = () => {
    if (action === "reject") {
      onReject(actionRow);
    } else if (action === "delete") {
      handleDelete(actionRow);
    } else {
      onTerminate(actionRow, action);
    }
    setAction("");
    setActionRow(null);
  };

  const handleCloseModal = () => {
    setAction("");
  };

  const handleSummaryClose = () => {
    setSummary(null);
    setSummaryAction("");
  };

  const renderActions = (row) => {
    const { status } = row;
    const {
      isApply,
      isApplyRejected,
      isDestroy,
      isDestroyCancel,
      isDestroyPlan,
    } = row.loader;

    let buttonData = [];
    const enableClone = [
      "COMPLETED",
      "TERMINATE_IN_PROGRESS",
      "TERMINATED",
    ].includes(status);

    let moreActionsData = [
      {
        title: "Edit Provision",
        label: "Edit",
        onClick: () => handleEdit(row),
        icon: <Edit />,
        color: "info",
        disabled: status === "TERMINATED",
      },
      {
        title: "Delete Provision",
        label: "Delete",
        onClick: () => openConfirmDialog(row, "delete"),
        icon: <DeleteOutline />,
        color: "error",
        disabled: false,
      },
      {
        title: "Clone Provision",
        label: "Clone",
        onClick: () => handleClone(row),
        icon: <AddToPhotos />,
        color: "secondary",
        disabled: !enableClone,
      },
    ];

    const applyBtn = { label: "Apply", disabled: true, loading: isApply };
    const rejectBtn = {
      label: status === "TERMINATE_IN_PROGRESS" ? "Cancel" : "Reject",
      disabled: true,
      loading:
        status === "TERMINATE_IN_PROGRESS" ? isDestroyCancel : isApplyRejected,
    };
    const terminateLabel =
      status === "TERMINATE_IN_PROGRESS" ? "Destroy" : "Terminate";
    const terminateBtn = {
      title: terminateLabel,
      label: terminateLabel,
      onClick: () => handleClick(terminateLabel),
      icon: <BlockOutlined />,
      disabled: true,
      loading: status === "TERMINATE_IN_PROGRESS" ? isDestroy : isDestroyPlan,
      color: "error",
    };

    if (status === "IN_PROGRESS") {
      buttonData.push({ ...applyBtn, disabled: false }); // for testing the changes
      buttonData.push({ ...rejectBtn, disabled: false });
      moreActionsData.push(terminateBtn);
    }
    if (
      [
        "FAILED",
        "APPLY_FAILED",
        "TERMINATE_PLAN_FAILED",
        "TERMINATE_FAILED",
      ].includes(status)
    ) {
      buttonData.push(applyBtn);
      buttonData.push({ ...rejectBtn, disabled: false });
      moreActionsData.push(terminateBtn);
    }
    if (status === "COMPLETED") {
      buttonData.push(applyBtn);
      buttonData.push(rejectBtn);
      moreActionsData.push({ ...terminateBtn, disabled: false });
    }
    if (status === "TERMINATE_IN_PROGRESS") {
      buttonData.push(applyBtn);
      buttonData.push({ ...rejectBtn, disabled: false });
      moreActionsData.push({ ...terminateBtn, disabled: false });
    }
    if (["REJECTED", "TERMINATED"].includes(status)) {
      buttonData.push(applyBtn);
      buttonData.push(rejectBtn);
      moreActionsData.push(terminateBtn);
    }

    const handleClick = (label) => {
      if (label === "Apply") {
        onApply(row);
      } else if (["Cancel", "Reject"].includes(label)) {
        openConfirmDialog(row, label.toLowerCase());
      } else {
        openConfirmDialog(row, label.toLowerCase());
      }
    };

    const handleEdit = (data) => {
      history.push({
        pathname: "/provision/edit",
        state: data,
      });
    };

    const handleClone = (data) => {
      history.push({
        pathname: "/provision/clone",
        state: { ...data, name: `${data.name}-clone` },
      });
    };

    const renderMoreActions = () => {
      return (
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"start"}
          gap={1}
          p={1}
        >
          {moreActionsData.map((data) => (
            <IconButton
              title={data.title}
              onClick={data.onClick}
              color={data.color}
              size="small"
              disabled={data.disabled || data.loading}
              loading={data.loading}
              sx={{
                fontFamily: "inherit",
                ":hover": {
                  backgroundColor: "unset",
                },
              }}
            >
              {data.loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                data.icon
              )}
              <Box ml={1}>{data.label}</Box>
            </IconButton>
          ))}
        </Box>
      );
    };

    return (
      <Box sx={{ display: "flex", gridGap: "12px" }}>
        {buttonData.map((data) => (
          <LoadingButton
            {...data}
            size="small"
            disabled={!isAdmin || data.disabled || data.loading}
            variant="outlined"
            onClick={() => handleClick(data.label)}
            color={
              data.label === "Apply"
                ? "success"
                : ["Reject", "Terminate", "Destroy"].includes(data.label)
                ? "error"
                : "info"
            }
          />
        ))}
        <Box sx={{ marginLeft: "auto" }}>
          <MoreActions title={"More Actions"} content={renderMoreActions()} />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <LogDetails data={logData} onClose={() => setLogData(null)} />
      <CheckovDetails data={rowDetails} onClose={() => setRowDetails(null)} />
      <CustomTable columns={columns} data={data} defaultPageSize={10} />
      <AccountSummary
        summary={summary}
        label={summaryAction}
        onClose={handleSummaryClose}
      />
      <ActionDialog
        open={action}
        onClose={handleCloseModal}
        action={capitalizeWords(action)}
        onAction={handleConfirmedAction}
        actionItem={"provision"}
      />
    </>
  );
};

export default ProvisionTable;
